import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api';

export default function TraceabilityModal({ traceId, onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/logs/traceability/${traceId}`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [traceId]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl bg-slate-950 border border-slate-700/80 shadow-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-cyan-400/80">
              {t('presence.traceabilityTitle')}
            </div>
            <div className="text-sm text-slate-200">{t('presence.responseDiagnostics')}</div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-100 text-sm"
          >
            ✕
          </button>
        </div>

        {loading && <div className="text-sm text-slate-400">{t('presence.loading')}</div>}
        {!loading && !data && (
          <div className="text-sm text-slate-400">{t('presence.noTraceabilityRecord')}</div>
        )}

        {!loading && data && (
          <div className="space-y-4 text-xs text-slate-200">
            <Section title={t('presence.inputOutput')}>
              <Field label={t('presence.userMessage')} value={data.input_text} />
              <Field label={t('presence.response')} value={data.output_text} />
            </Section>

            <Section title={t('presence.memoriesUsed')}>
              {(!data.memory_ids || data.memory_ids.length === 0) && (
                <div className="text-slate-400">{t('presence.noMemoriesInjected')}</div>
              )}
              {data.memory_ids && data.memory_ids.length > 0 && (
                <ul className="list-disc pl-4 text-slate-300">
                  {data.memory_ids.map((id, idx) => (
                    <li key={id}>
                      {id} <span className="text-slate-500 text-[10px]">({data.memory_types?.[idx]})</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title={t('presence.governance')}>
              <Field
                label={t('presence.categoriesTriggered')}
                value={
                  data.governance_categories_triggered?.length
                    ? data.governance_categories_triggered.join(', ')
                    : t('presence.none')
                }
              />
            </Section>

            <Section title={t('presence.personaSnapshot')}>
              <pre className="bg-slate-900/80 rounded-lg border border-slate-700 px-3 py-2 text-[10px] text-slate-300 overflow-x-auto">
                {JSON.stringify(data.persona_snapshot || {}, null, 2)}
              </pre>
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-0.5">
        {label}
      </div>
      <div className="text-xs text-slate-200 whitespace-pre-wrap">{value}</div>
    </div>
  );
}
